import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Star, RefreshCw, Send, Globe, EyeOff, Copy, Check,
  Printer, MessageSquare, QrCode, ChevronRight, Mail, Phone,
  TrendingUp, Filter, X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api/axios';
import { useToast } from './Toast';

/* ── Helpers ──────────────────────────────────────────────────────────── */
function fmt(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StarRow({ rating, size = 13 }) {
  return (
    <span className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={size}
          className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200 dark:text-slate-700'} />
      ))}
    </span>
  );
}

const AVATAR_COLORS = [
  ['#f59e0b','#fef3c7'],['#10b981','#d1fae5'],['#6366f1','#ede9fe'],
  ['#f43f5e','#ffe4e6'],['#0ea5e9','#e0f2fe'],['#8b5cf6','#ede9fe'],
];
function avatarColor(name) {
  const i = (name || '?').charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[i];
}

/* ── Carte avis ───────────────────────────────────────────────────────── */
function AvisCard({ avis, onReplyDone }) {
  const showToast = useToast();
  const [open,    setOpen]    = useState(false);
  const [reply,   setReply]   = useState(avis.adminReply || '');
  const [saving,  setSaving]  = useState(false);
  const [hovered, setHovered] = useState(false);

  const [fg, bg] = avatarColor(avis.clientNom);
  const initial  = (avis.clientNom || '?').charAt(0).toUpperCase();
  const unanswered = !avis.adminReply;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/avis/${avis._id}/reply`, { reply });
      showToast('Réponse enregistrée', 'success');
      onReplyDone();
      setOpen(false);
    } catch { showToast('Erreur', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative bg-white dark:bg-slate-800/70 rounded-2xl border transition-all duration-200 overflow-hidden
        ${hovered ? 'shadow-lg -translate-y-0.5' : 'shadow-sm'}
        ${unanswered
          ? 'border-amber-200 dark:border-amber-700/40'
          : 'border-slate-100 dark:border-slate-700/50'}`}
    >
      {/* Bande latérale colorée */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${unanswered ? 'bg-amber-400' : 'bg-emerald-400'}`} />

      <div className="pl-5 pr-5 pt-5 pb-4">
        {/* Row 1 : avatar + nom + date + badge + étoiles */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-base font-black flex-shrink-0"
              style={{ background: bg, color: fg }}>
              {initial}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{avis.clientNom}</p>
                {unanswered && (
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0">
                    Sans réponse
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{fmt(avis.submittedAt)}</p>
            </div>
          </div>
          <div className="flex-shrink-0 flex flex-col items-end gap-1">
            <StarRow rating={avis.rating} />
            <span className="text-[11px] font-black text-amber-500">{avis.rating}.0</span>
          </div>
        </div>

        {/* Commentaire */}
        {avis.comment && (
          <blockquote className="mt-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed
            bg-slate-50 dark:bg-slate-700/30 rounded-xl px-4 py-3 italic border-l-2 border-slate-200 dark:border-slate-600">
            "{avis.comment}"
          </blockquote>
        )}

        {/* Coordonnées client */}
        {(avis.clientEmail || avis.clientTelephone) && (
          <div className="mt-3 flex flex-wrap gap-3">
            {avis.clientEmail && (
              <a href={`mailto:${avis.clientEmail}`}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600 transition">
                <Mail size={11} /> {avis.clientEmail}
              </a>
            )}
            {avis.clientTelephone && (
              <a href={`tel:${avis.clientTelephone}`}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600 transition">
                <Phone size={11} /> {avis.clientTelephone}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Réponse existante */}
      {avis.adminReply && !open && (
        <div className="mx-5 mb-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200/60 dark:border-teal-700/40 rounded-xl px-4 py-3">
          <p className="text-[10px] font-black uppercase tracking-wider text-teal-600 dark:text-teal-400 mb-1.5">
            Votre réponse
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">"{avis.adminReply}"</p>
        </div>
      )}

      {/* Zone de saisie ouverte */}
      {open && (
        <div className="px-5 pb-5 space-y-3">
          <textarea
            rows={3}
            value={reply}
            onChange={e => setReply(e.target.value)}
            autoFocus
            placeholder="Répondez à cet avis de manière professionnelle…"
            className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50
              px-4 py-3 text-sm text-slate-900 dark:text-white placeholder-slate-400
              focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none transition"
          />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving || !reply.trim()}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl
                hover:bg-teal-700 disabled:opacity-40 transition">
              <Send size={11} /> {saving ? '…' : 'Publier'}
            </button>
            <button onClick={() => { setOpen(false); setReply(avis.adminReply || ''); }}
              className="px-3 py-2 text-xs text-slate-500 rounded-xl border border-slate-200 dark:border-slate-600
                hover:bg-slate-50 dark:hover:bg-slate-700 transition flex items-center gap-1">
              <X size={11} /> Annuler
            </button>
          </div>
        </div>
      )}

      {/* Footer actions */}
      {!open && (
        <div className="px-5 pb-4">
          <button onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400
              hover:text-teal-700 transition group">
            <MessageSquare size={12} className="group-hover:scale-110 transition-transform" />
            {avis.adminReply ? 'Modifier la réponse' : 'Répondre à cet avis'}
            <ChevronRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ── QR Permanent ─────────────────────────────────────────────────────── */
function QRPermanent({ slug, salonNom }) {
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);
  const url = `${window.location.origin}/avis/salon/${slug}`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const print = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head>
      <title>QR Avis - ${salonNom}</title>
      <style>
        body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
             min-height:100vh;font-family:system-ui,sans-serif;background:#fff;color:#1C1814;padding:40px;box-sizing:border-box;}
        h1{font-size:28px;font-weight:800;margin:0 0 6px;}
        p{font-size:14px;color:#6B6560;margin:0 0 28px;text-align:center;}
        .qr{padding:24px;border:2px solid #E4DED6;border-radius:20px;}
        .url{margin-top:18px;font-size:10px;color:#A8A3A0;word-break:break-all;max-width:240px;text-align:center;}
        @media print{body{padding:20px;}}
      </style></head><body>
      <h1>${salonNom}</h1>
      <p>Scannez pour laisser un avis</p>
      <div class="qr">${printRef.current?.innerHTML || ''}</div>
      <p class="url">${url}</p>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  return (
    <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <QrCode size={16} className="text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-800 dark:text-white">QR Code permanent</p>
          <p className="text-xs text-slate-400">Imprimez et affichez-le dans votre salon</p>
        </div>
      </div>

      <div className="flex gap-5 items-center">
        <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-inner flex-shrink-0" ref={printRef}>
          <QRCodeSVG value={url} size={110} level="M" fgColor="#312e81" />
        </div>
        <div className="flex-1 space-y-3">
          <p className="text-[11px] text-slate-400 font-mono break-all bg-slate-50 dark:bg-slate-700/50 px-3 py-2 rounded-lg leading-relaxed">
            {url}
          </p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={copy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600
                text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition">
              {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
              {copied ? 'Copié !' : 'Copier'}
            </button>
            <button onClick={print}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition">
              <Printer size={12} /> Imprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Page principale ──────────────────────────────────────────────────── */
export default function AdminAvis() {
  const showToast = useToast();
  const [avis,       setAvis]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [publicMode, setPublicMode] = useState(false);
  const [toggling,   setToggling]   = useState(false);
  const [salonSlug,  setSalonSlug]  = useState('');
  const [salonNom,   setSalonNom]   = useState('');
  const [filter,     setFilter]     = useState('all'); // 'all' | 'unanswered' | 1..5

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [avisRes, salonRes] = await Promise.all([
        api.get('/avis'),
        api.get('/salon/me'),
      ]);
      setAvis(avisRes.data.data || []);
      setPublicMode(salonRes.data.data?.avisPublics || false);
      setSalonSlug(salonRes.data.data?.bookingSlug || '');
      setSalonNom(salonRes.data.data?.nom || '');
    } catch { showToast('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setToggling(true);
    try {
      const { data } = await api.put('/avis/settings/toggle', { avisPublics: !publicMode });
      setPublicMode(data.data.avisPublics);
      showToast(data.data.avisPublics ? 'Avis visibles sur la page de réservation' : 'Avis masqués', 'success');
    } catch { showToast('Erreur', 'error'); }
    finally { setToggling(false); }
  };

  /* Stats */
  const total      = avis.length;
  const avg        = total ? (avis.reduce((s, a) => s + a.rating, 0) / total) : 0;
  const avgDisplay = avg.toFixed(1);
  const answered   = avis.filter(a => a.adminReply).length;
  const unanswered = total - answered;
  const responseRate = total ? Math.round((answered / total) * 100) : 0;
  const dist = [5,4,3,2,1].map(n => ({ n, count: avis.filter(a => a.rating === n).length }));

  /* Filtres */
  const visible = avis.filter(a => {
    if (filter === 'unanswered') return !a.adminReply;
    if (typeof filter === 'number') return a.rating === filter;
    return true;
  });

  const FILTERS = [
    { key: 'all',        label: 'Tous',          count: total },
    { key: 'unanswered', label: 'Sans réponse',  count: unanswered },
    ...([5,4,3,2,1].map(n => ({ key: n, label: `${n}★`, count: dist.find(d => d.n === n)?.count || 0 }))),
  ];

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-300">

      {/* ── Banner stats ─────────────────────────────────────────────── */}
      <div className="rounded-3xl overflow-hidden bg-slate-900 dark:bg-slate-950 shadow-xl relative">

        {/* Texture subtile */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #f59e0b 0%, transparent 60%), radial-gradient(circle at 80% 20%, #6366f1 0%, transparent 50%)' }} />

        <div className="relative px-6 pt-6 pb-8">
          {/* Header du banner */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-black text-white tracking-tight">Avis clients</h1>
              <p className="text-slate-400 text-xs mt-0.5">Consultez et répondez aux retours de vos clients</p>
            </div>
            <button onClick={load}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition border border-white/10">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Note moyenne */}
            <div className="bg-white/8 rounded-2xl p-4 border border-white/10">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Note moyenne</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-black text-amber-400 leading-none">{total ? avgDisplay : '-'}</span>
                <span className="text-slate-500 text-sm mb-0.5">/5</span>
              </div>
              {total > 0 && <div className="mt-2"><StarRow rating={Math.round(avg)} size={11} /></div>}
            </div>

            {/* Total avis */}
            <div className="bg-white/8 rounded-2xl p-4 border border-white/10">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Total avis</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-black text-white leading-none">{total}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">{answered} avec réponse</p>
            </div>

            {/* Taux de réponse */}
            <div className="bg-white/8 rounded-2xl p-4 border border-white/10">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Taux de réponse</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-black text-emerald-400 leading-none">{responseRate}</span>
                <span className="text-slate-500 text-sm mb-0.5">%</span>
              </div>
              <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${responseRate}%` }} />
              </div>
            </div>

            {/* Sans réponse */}
            <div className={`rounded-2xl p-4 border transition-all ${unanswered > 0
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-white/8 border-white/10'}`}>
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Sans réponse</p>
              <div className="flex items-end gap-2">
                <span className={`text-4xl font-black leading-none ${unanswered > 0 ? 'text-amber-400' : 'text-white'}`}>
                  {unanswered}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {unanswered > 0 ? 'En attente de réponse' : 'Tout est traité 🎉'}
              </p>
            </div>
          </div>

          {/* Distribution */}
          {total > 0 && (
            <div className="mt-5 grid grid-cols-5 gap-2">
              {[5,4,3,2,1].map(n => {
                const count = dist.find(d => d.n === n)?.count || 0;
                const pct   = total ? (count / total) * 100 : 0;
                return (
                  <button key={n} onClick={() => setFilter(filter === n ? 'all' : n)}
                    className={`flex flex-col items-center gap-1.5 py-2 rounded-xl border transition-all
                      ${filter === n
                        ? 'bg-amber-500/20 border-amber-500/40'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                    <span className="text-[10px] font-bold text-slate-400">{n}★</span>
                    <div className="w-full px-2">
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="text-xs font-black text-white">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Deux colonnes ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Colonne gauche : QR + visibilité */}
        <div className="space-y-4">
          {/* Toggle visibilité */}
          <div className={`p-4 rounded-2xl border-2 transition-all
            ${publicMode
              ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400/60'
              : 'bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                ${publicMode ? 'bg-teal-100 dark:bg-teal-900/40' : 'bg-slate-100 dark:bg-slate-700'}`}>
                {publicMode
                  ? <Globe size={17} className="text-teal-600" />
                  : <EyeOff size={17} className="text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold leading-snug ${publicMode ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>
                  {publicMode ? 'Avis publics' : 'Avis masqués'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {publicMode ? 'Visibles sur la page de réservation' : 'Non affichés publiquement'}
                </p>
              </div>
              <button onClick={toggle} disabled={toggling}
                className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${publicMode ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${publicMode ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </div>

          {/* QR permanent */}
          {!loading && salonSlug && <QRPermanent slug={salonSlug} salonNom={salonNom} />}
        </div>

        {/* Colonne droite : liste des avis */}
        <div className="lg:col-span-2 space-y-4">

          {/* Filtres */}
          {total > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              <Filter size={13} className="text-slate-400 flex-shrink-0" />
              {FILTERS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key === filter ? 'all' : f.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all border
                    ${filter === f.key
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                      : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-400'}`}>
                  {f.label}
                  {f.count > 0 && (
                    <span className={`rounded-full w-4 h-4 flex items-center justify-center text-[9px]
                      ${filter === f.key ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                      {f.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Contenu */}
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-32 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
              ))}
            </div>
          ) : total === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center
              rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-4">
                <Star size={28} className="text-slate-300 dark:text-slate-600" />
              </div>
              <p className="font-bold text-slate-500 dark:text-slate-400">Aucun avis pour l'instant</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 max-w-xs">
                Partagez votre QR code ou envoyez un lien après chaque prestation pour collecter des avis.
              </p>
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center
              rounded-2xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/40">
              <TrendingUp size={24} className="text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-400">Aucun avis pour ce filtre</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map(a => (
                <AvisCard key={a._id} avis={a} onReplyDone={load} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
